<script setup>
import { ref, computed, watch, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  existingFile: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['update:file', 'update:removeExisting']);

const { t } = useI18n();

const fileInputRef = ref(null);
const selectedFile = ref(null);
const objectPreviewUrl = ref(null);
const removeExisting = ref(false);

const previewUrl = computed(() => {
  if (objectPreviewUrl.value) return objectPreviewUrl.value;
  if (removeExisting.value) return null;
  return props.existingFile?.file_url || null;
});

const revokeObjectUrl = () => {
  if (objectPreviewUrl.value) {
    URL.revokeObjectURL(objectPreviewUrl.value);
    objectPreviewUrl.value = null;
  }
};

watch(selectedFile, file => {
  revokeObjectUrl();
  if (!file) return;
  objectPreviewUrl.value = URL.createObjectURL(file);
  emit('update:file', file);
  emit('update:removeExisting', false);
});

watch(removeExisting, value => {
  emit('update:removeExisting', value);
  if (value) emit('update:file', null);
});

onBeforeUnmount(revokeObjectUrl);

const openFilePicker = () => {
  fileInputRef.value?.click();
};

const onFileChange = event => {
  const file = event.target.files?.[0];
  if (!file) return;
  removeExisting.value = false;
  selectedFile.value = file;
  event.target.value = '';
};

const removeImage = () => {
  selectedFile.value = null;
  revokeObjectUrl();
  if (props.existingFile?.file_url) {
    removeExisting.value = true;
    return;
  }
  emit('update:file', null);
  emit('update:removeExisting', false);
};
</script>

<template>
  <div class="flex flex-col gap-2">
    <label class="text-sm font-medium text-n-slate-12">
      {{ t('CANNED_MGMT.FORM.IMAGE.LABEL') }}
    </label>
    <input
      ref="fileInputRef"
      type="file"
      accept="image/*"
      class="hidden"
      @change="onFileChange"
    />
    <div
      v-if="previewUrl"
      class="relative inline-flex w-fit max-w-full rounded-lg border border-n-weak overflow-hidden"
    >
      <img
        :src="previewUrl"
        :alt="t('CANNED_MGMT.FORM.IMAGE.PREVIEW_ALT')"
        class="max-h-40 object-contain"
      />
      <Button
        type="button"
        icon="i-lucide-x"
        xs
        slate
        class="absolute top-1 right-1"
        @click="removeImage"
      />
    </div>
    <Button
      v-if="!previewUrl"
      type="button"
      faded
      slate
      sm
      icon="i-lucide-image-plus"
      :label="t('CANNED_MGMT.FORM.IMAGE.ADD')"
      @click="openFilePicker"
    />
    <Button
      v-else
      type="button"
      faded
      slate
      sm
      icon="i-lucide-image-plus"
      :label="t('CANNED_MGMT.FORM.IMAGE.REPLACE')"
      @click="openFilePicker"
    />
    <p class="text-xs text-n-slate-11">
      {{ t('CANNED_MGMT.FORM.IMAGE.HELP') }}
    </p>
  </div>
</template>
